import React, { useEffect, useMemo, useRef, useState } from "react";
import { signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import "../../App.css";
import { useSpring, animated } from "@react-spring/web";
import PhoneStep from "./PhoneStep";
import CodeStep from "./CodeStep";
import ProfileStep from "./ProfileStep";
import { auth } from "../../Firebase";
import { useTranslation } from "../../i18n/useTranslation";
import { useAuth } from "../../contexts/AuthContext";

export default function AuthModal({ setIsOpen, onSuccess }) {
  const { t } = useTranslation();
  const {
    finalizeSignup,
    afterPhoneOtpLogin,
    userProfile,
    userProfileLoading,
    isProfileComplete,
  } = useAuth();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [codeError, setCodeError] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [smsCooldown, setSmsCooldown] = useState(0);
  const [isOtpFlow, setIsOtpFlow] = useState(false);
  const [hasVerifiedOtp, setHasVerifiedOtp] = useState(false);

  const [form, setForm] = useState({
    phone: "",
    phoneE164: "",
    code: "",
    firstName: "",
    email: "",
    address: "",
    promoOptOut: false,
  });

  const springs = useSpring({ from: { y: 600 }, to: { y: 0 } });
  const isDev = process.env.NODE_ENV === "development";
  const lockRef = useRef(false);
  const recaptchaVerifierRef = useRef(null);

  const fakeVerifier = useMemo(() => {
    return {
      type: "recaptcha",
      verify: async () => "dev-bypass-token",
      clear: () => {},
      _reset: () => {},
    };
  }, []);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const back = () => setStep((prev) => Math.max(0, prev - 1));

  const close = () => {
    setIsOtpFlow(false);
    setHasVerifiedOtp(false);
    setStep(0);
    setIsOpen(false);
  };

  const toFrenchE164 = (raw) => {
    const v = (raw || "").replace(/\s+/g, "");
    if (/^0[1-9]\d{8}$/.test(v)) return `+33${v.slice(1)}`;
    if (/^\+33[1-9]\d{8}$/.test(v)) return v;
    return null;
  };

  const prefillFormWithProfile = (profile, firebaseUser) => {
    setForm((prev) => ({
      ...prev,
      phoneE164: firebaseUser?.phoneNumber || prev.phoneE164,
      phone: firebaseUser?.phoneNumber || prev.phone,
      firstName: profile?.firstName || "",
      email: profile?.email || "",
      address: profile?.address || "",
      promoOptOut:
        typeof profile?.promoOptOut === "boolean" ? profile.promoOptOut : false,
    }));
  };

  const sendOtp = async (maybeE164) => {
    if (lockRef.current || smsCooldown > 0) return;
    lockRef.current = true;

    setPhoneError("");
    setCodeError("");

    const e164 = maybeE164 || toFrenchE164(form.phone);

    if (!e164) {
      setPhoneError(t("phone_invalid"));
      lockRef.current = false;
      return;
    }

    try {
      setLoading(true);
      setIsOtpFlow(true);
      setHasVerifiedOtp(false);

      const appVerifier = isDev ? fakeVerifier : recaptchaVerifierRef.current;
      const result = await signInWithPhoneNumber(auth, e164, appVerifier);

      setConfirmationResult(result);
      update({ phoneE164: e164 });
      setStep(1);
    } catch (err) {
      console.error("signInWithPhoneNumber error:", err);
      setPhoneError(t("sms_send_failed"));
    } finally {
      setLoading(false);
      lockRef.current = false;
    }
  };

  const verifyOtp = async () => {
    setCodeError("");

    if (!confirmationResult) {
      setCodeError(t("sms_not_sent"));
      return;
    }

    const clean = (form.code || "").trim();

    if (!/^\d{4,6}$/.test(clean)) {
      setCodeError(t("code_invalid"));
      return;
    }

    try {
      setLoading(true);

      const cred = await confirmationResult.confirm(clean);
      const profile = await afterPhoneOtpLogin(cred.user);

      if (profile && isProfileComplete(profile) && !profile.__missingDoc) {
        onSuccess?.(profile);
        close();
        return;
      }

      setHasVerifiedOtp(true);
      setIsOtpFlow(false);
      prefillFormWithProfile(profile, cred.user);
      setStep(2);
    } catch (err) {
      console.error("confirm code error:", err);
      setCodeError(t("code_incorrect"));
    } finally {
      setLoading(false);
    }
  };

  const handleDone = async () => {
    const user = auth.currentUser;
    if (!user?.uid) {
      close();
      return;
    }

    setLoading(true);

    try {
      const payload = await finalizeSignup({
        user,
        role: "customer",
        authProvider: "phone",
        profile: {
          phone: form.phoneE164 || form.phone,
          email: (form.email || "").trim() || null,
          firstName: (form.firstName || "").trim() || null,
          address: (form.address || "").trim() || null,
          promoOptOut:
            typeof form.promoOptOut === "boolean" ? form.promoOptOut : null,
        },
      });

      setHasVerifiedOtp(false);
      setIsOtpFlow(false);
      onSuccess?.(payload);
      close();
    } catch (e) {
      console.error("finalizeSignup phone error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setStep(0);
    setPhoneError("");
    setCodeError("");
    setConfirmationResult(null);
    setSmsCooldown(0);
    setIsOtpFlow(false);
    setHasVerifiedOtp(false);

    setForm({
      phone: "",
      phoneE164: "",
      code: "",
      firstName: "",
      email: "",
      address: "",
      promoOptOut: false,
    });
  }, []);

  useEffect(() => {
    if (!isDev && !recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        { size: "invisible" }
      );
    }

    return () => {
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
    };
  }, [isDev]);

  useEffect(() => {
    if (smsCooldown <= 0) return;

    const timer = setTimeout(() => {
      setSmsCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [smsCooldown]);

  useEffect(() => {
    if (step === 1 && confirmationResult && smsCooldown === 0) {
      setSmsCooldown(30);
    }
  }, [step, confirmationResult, smsCooldown]);

  useEffect(() => {
    if (hasVerifiedOtp) return;
    if (isOtpFlow) return;

    const currentFirebaseUser = auth.currentUser;
    if (!currentFirebaseUser) return;
    if (userProfileLoading) return;

    if (isProfileComplete(userProfile) && !userProfile?.__missingDoc) {
      close();
      return;
    }

    prefillFormWithProfile(userProfile, currentFirebaseUser);
    setStep(2);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, userProfileLoading, isOtpFlow, hasVerifiedOtp, isProfileComplete]);

  return (
    <div className="product-modal-container">
      <animated.div
        className="product-modal-content modal-options"
        style={{ ...springs }}
        onClick={(e) => e.stopPropagation()}
      >
        {step === 0 && (
          <PhoneStep
            phone={form.phone}
            setPhone={(v) => update({ phone: v })}
            onSendOtp={sendOtp}
            onClose={close}
            loading={loading || userProfileLoading}
            serverError={phoneError}
            clearServerError={() => setPhoneError("")}
            smsCooldown={smsCooldown}
          />
        )}

        {step === 1 && (
          <CodeStep
            phoneE164={form.phoneE164 || form.phone}
            code={form.code}
            setCode={(v) => update({ code: v })}
            onBack={back}
            onVerifyOtp={verifyOtp}
            onClose={close}
            loading={loading || userProfileLoading}
            error={codeError}
          />
        )}

        {step === 2 && (
          <ProfileStep
            firstName={form.firstName}
            email={form.email}
            address={form.address}
            promoOptOut={form.promoOptOut}
            onChange={update}
            onBack={back}
            onDone={handleDone}
            onClose={close}
            loading={loading || userProfileLoading}
          />
        )}

        <div id="recaptcha-container" />
      </animated.div>
    </div>
  );
}